'use client';

import * as React from 'react';
import { c } from '../../utils/tailwind-merge';
import { TableBodyProps } from './types';

function Table({
  className = '',
  ...props
}: React.ComponentPropsWithoutRef<'table'>) {
  return (
    <div
      data-slot="table-container"
      className={c(
        'relative w-full border-separate border-spacing-0 overflow-x-auto tracking-[-0.028px]'
      )}
    >
      <table
        data-slot="table"
        className={c('w-full table-auto text-sm', className)}
        {...props}
      />
    </div>
  );
}

function TableBody({
  variant,
  className = '',
  rightColTextAlign = 'right',
  ...props
}: TableBodyProps) {
  const alternateTableColors =
    '[&>tr:nth-child(even)]:bg-white [&>tr:nth-child(odd)>td:first-child]:rounded-l-lg [&>tr:nth-child(odd)>td:last-child]:rounded-r-lg [&>tr:nth-child(odd)]:bg-grey-100';
  const alternateTablePx = `[&>tr>td:first-child]:pl-[11px]
    [&>tr>td:last-child]:pr-[10px]
    [&>tr>td]:pb-[13px] [&>tr>td]:pt-[9px]`;

  return (
    <tbody
      data-slot="table-body"
      className={c(
        variant === 'alternate'
          ? `${alternateTableColors} ${alternateTablePx}`
          : '[&>tr>td]:px-0 [&>tr>td]:py-1',
        'break-all [&>tr>td:first-child]:gap-x-1 [&>tr>td:first-child]:text-nowrap [&>tr>td:first-child]:align-top [&>tr>td:first-child]:text-grey-700',
        rightColTextAlign === 'right'
          ? '[&>tr>td:last-child]:text-right'
          : '[&>tr>td:last-child]:text-left',
        '[&>tr>td:last-child]:text-grey-900',
        className
      )}
      {...props}
    />
  );
}

function TableRow({ className = '', ...props }: React.ComponentProps<'tr'>) {
  return <tr data-slot="table-row" className={className} {...props} />;
}

function TableCell({ className = '', ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={c(
        '[&_span]:tiny-text-link [&_span]:flex [&_span]:justify-end [&_span]:text-blue-hover', // Support for LinkButton styles
        '[&_svg]:ml-1',
        'break-all',
        className
      )}
      {...props}
    />
  );
}

function TableCaption({
  className = '',
  ...props
}: React.ComponentPropsWithoutRef<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={c(
        'tiny-text text-muted-foreground mb-4 caption-top break-all text-left font-medium leading-5',
        className
      )}
      {...props}
    />
  );
}

export { Table, TableBody, TableCaption, TableCell, TableRow };
